import { plural, t } from "@lingui/macro";
import React, { ComponentProps, useRef } from "react";
import { FlatList } from "react-native";

import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { Menu } from "../../overlay/Menu";
import { Trigger } from "../Trigger/Trigger";
import { Group } from "./components/Group";
import { Item } from "./components/Item";
import {
  DropDownSize,
  DropdownSelectOption,
  DropdownSelectOptionsGroup,
} from "./index";

export type MultiSelectCheckboxProps = {
  size?: DropDownSize;
  label?: string;
  subMessage?: string;
  placeholder?: string;
  values: string[];
  options:
    | DropdownSelectOptionsGroup<string>[]
    | DropdownSelectOption<string>[];
  onChange: (values: (string | null)[]) => void;
  testID?: string;
  ariaLabel?: string;
  hasError?: boolean;
  isDisabled?: boolean;
  showSelectAll?: boolean;
  showClearAll?: boolean;
  wrapperProps?: ComponentProps<typeof Box>;
  expandMenu?: boolean;
  variant?: "default" | "compact";
  customTrigger?: (props: {
    isOpen: boolean;
    onPress: () => void;
  }) => React.JSX.Element;
};

export const MultiSelectCheckbox = ({
  size = "regular",
  label,
  subMessage,
  values,
  options,
  onChange,
  placeholder,
  testID,
  ariaLabel,
  hasError,
  isDisabled,
  showSelectAll,
  showClearAll,
  variant = "default",
  expandMenu = false,
  customTrigger,
}: MultiSelectCheckboxProps) => {
  const isGrouped = options[0] && options[0].hasOwnProperty("options");

  const groupedOptions = isGrouped
    ? (options as DropdownSelectOptionsGroup<string>[])
    : [{ label: "", options: options as DropdownSelectOption<string>[] }];

  const selectedItems = getMultiValueIndexesFromString(
    values || "",
    groupedOptions
  );

  const optionsCount = groupedOptions.reduce(
    (sum, item) => sum + item.options.length,
    0
  );

  const isSelectAll = selectedItems.length < optionsCount && optionsCount > 0;
  const selectedCount = selectedItems.length;

  const displayValue = selectedCount
    ? selectedCount > 1
      ? plural(selectedCount, {
          one: "# selected",
          other: "# selected",
        })
      : selectedItems[0].label
    : placeholder ?? "";

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const onOptionClick = (option: string | null) => {
    if (option && values.includes(option)) {
      onChange(values.filter((selectedItem) => selectedItem !== option));
    } else {
      onChange([...values, option]);
    }
  };

  const selectAllInList = () => {
    const allEnabledValues = groupedOptions.flatMap((group) => {
      return group.options
        .filter((option) => !option.isDisabled)
        .map((option) => option.value);
    });

    if (
      allEnabledValues.every((value) =>
        value ? values.includes(value) : false
      )
    ) {
      onChange(values.filter((value) => !allEnabledValues.includes(value)));
    } else {
      onChange([...allEnabledValues]);
    }
  };

  const clearSelection = () => {
    onChange([]);
  };

  const selectAllInGroup = (group: DropdownSelectOptionsGroup<string>) => {
    const groupValues = group.options.map((item) => item.value);
    if (
      groupValues.every((value) => (value ? values.includes(value) : false))
    ) {
      onChange(values.filter((value) => !groupValues.includes(value)));
    } else {
      onChange([...groupValues]);
    }
  };

  const triggerSelectedItem: DropdownSelectOption<any> = {
    label: displayValue,
    value: displayValue,
  };

  return (
    <Box flex={1} ref={wrapperRef}>
      {label ? (
        <Text
          mt={1}
          pb={2}
          variant="mainTiny"
          color={hasError ? "text-error" : "text-secondary"}
        >
          {label}
        </Text>
      ) : null}
      <VStack>
        <Menu
          showActionSheetDoneButton
          trigger={
            customTrigger
              ? customTrigger
              : ({ isOpen, onPress }) => {
                  return (
                    <Box>
                      <Trigger
                        ariaLabel={ariaLabel}
                        size={size}
                        onPress={() => {
                          if (!isDisabled) {
                            onPress();
                          }
                        }}
                        testID={testID}
                        placeholder={placeholder ?? ""}
                        selectedItem={triggerSelectedItem}
                        isDisabled={isDisabled}
                        hasError={hasError}
                        includeLeftElementInSelectedValue={false}
                        hideChevron={false}
                        hideLabelInDisplay={false}
                        hideSubLabelInDisplay={false}
                        variant={variant}
                      />
                    </Box>
                  );
                }
          }
          content={({ closeMenu }) => {
            return (
              <Menu.Content
                size={
                  variant === "compact" || expandMenu ? "large" : "matchTrigger"
                }
              >
                <FlatList
                  data={groupedOptions}
                  ListHeaderComponent={
                    showSelectAll ? (
                      <Item
                        isSelected={false}
                        item={{
                          label: isSelectAll ? t`Select All` : t`Deselect All`,
                          value: "select-all",
                          showUnderline: true,
                        }}
                        onPress={() => selectAllInList()}
                        testID={testID ? `${testID}-select-all` : `select-all`}
                      />
                    ) : showClearAll ? (
                      <Item
                        isSelected={false}
                        item={{
                          label: t`Clear`,
                          value: "clear-all",
                          showUnderline: true,
                        }}
                        onPress={() => clearSelection()}
                        testID={testID ? `${testID}-clear-all` : `clear-all`}
                      />
                    ) : null
                  }
                  keyExtractor={(item, index) => `${item.label}-${index}`}
                  renderItem={({ item, index }) => {
                    return (
                      <Group
                        isMulti
                        group={item}
                        selectedItems={selectedItems}
                        onItemPress={(value: string | null) => {
                          onOptionClick(value);
                        }}
                        selectAllInGroup={selectAllInGroup}
                        testID={
                          testID
                            ? `${testID}-group-${index}`
                            : `multi-select-checkbox-group-${index}`
                        }
                      />
                    );
                  }}
                />
              </Menu.Content>
            );
          }}
        />
      </VStack>
      {subMessage ? (
        hasError ? (
          <Text variant="mainTiny" color="text-error" fontWeight="600" mt={2}>
            {subMessage}
          </Text>
        ) : (
          <Text variant="mainTiny" color="text-secondary" mt={2}>
            {subMessage}
          </Text>
        )
      ) : null}
    </Box>
  );
};

const getMultiValueIndexesFromString = (
  values: string[],
  groupedOptions: DropdownSelectOptionsGroup<string>[]
) => {
  return groupedOptions
    .flatMap((group, groupIndex) => {
      return group.options.map((item, itemIndex) => {
        return { itemIndex, groupIndex, value: item.value, label: item.label };
      });
    })
    .filter((item) => (item.value ? values.includes(item.value) : false));
};
